import FormattedMessage from 'I18n/components/FormattedMessage';
import { StatusTag } from 'foundations-components/transitional/components/StatusTag';
import styled from 'foundations-theming-specialty/utils';
import { keyframes } from 'styled-components';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import FormattedDuration from 'I18n/components/FormattedDuration';
import { Flex } from 'foundations-components/components/Flex';
import { UserIcon } from 'foundations-assets/icons/components/UserIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const pulse = keyframes(["50%{opacity:0.5;}"]);
const LoadingCell = styled.div.withConfig({
  displayName: "formatCell__LoadingCell"
})(["width:100%;height:100%;display:flex;align-items:center;> div{border-radius:", ";background-color:", ";animation:", " 2s cubic-bezier(0.4,0,0.6,1) infinite;width:100%;}> div::before{content:'  ';display:inline-block;}"], ({
  theme
}) => theme.borderRadius[100], ({
  theme
}) => theme.color['fill-surface-recessed'], pulse);
export const formatDateTime = value => {
  const date = new Date(value);
  const friendlyDate = date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
  const expandedDateTime = date.toLocaleString(undefined, {
    hour: '2-digit',
    minute: '2-digit'
  });
  return /*#__PURE__*/_jsx("time", {
    dateTime: expandedDateTime,
    children: /*#__PURE__*/_jsx(Tooltip, {
      title: expandedDateTime,
      children: /*#__PURE__*/_jsx("span", {
        children: friendlyDate
      })
    })
  });
};

/**
 * Dates are stored as timestamps at midnight UTC. This function converts the timestamp to the date string in UTC timezone.
 * @param value
 */
export const formatDate = value => {
  const date = new Date(value);
  return date.toLocaleDateString(undefined, {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};
export const renderLoadingCell = __property => {
  return /*#__PURE__*/_jsx(LoadingCell, {
    children: /*#__PURE__*/_jsx("div", {})
  });
};
export const formatLoadingCell = __property => () => {
  return renderLoadingCell(__property);
};
export const getStage = row => {
  if (!row) return null;
  if (typeof row.hs_pipeline_stage === 'string') return row.hs_pipeline_stage;
  return null;
};
export const renderPropertyValue = (value, property, row, isGrid = false) => {
  const {
    crmPropertyType,
    propertyName,
    numberDisplayHint
  } = property;
  if (propertyName === '__requested_by_full_name_or_email' && isGrid) {
    const text = typeof value === 'string' ? value : '--';
    return /*#__PURE__*/_jsxs(Flex, {
      gap: "100",
      children: [/*#__PURE__*/_jsx(UserIcon, {
        size: "400"
      }), /*#__PURE__*/_jsx("span", {
        children: text
      })]
    });
  }

  // Prevents rendering an empty span or anchor tag if the value is an empty string
  if (value === '' || value === null || value === undefined) {
    return isGrid ? `--` : null;
  }
  if (['__requested_by_full_name_or_email', '__hubspot_owner_full_name'].includes(propertyName) && typeof value === 'string') {
    return /*#__PURE__*/_jsx("span", {
      children: value
    });
  }
  if (propertyName === 'last_ticket_activity' && typeof value === 'number') {
    return formatDateTime(value);
  }
  if (propertyName === 'status' && typeof value === 'string') {
    const variant = value === 'OPEN' ? 'oz' : 'disabled';
    const stage = isGrid ? getStage(row) : null;
    return /*#__PURE__*/_jsx(StatusTag, {
      use: variant,
      children: /*#__PURE__*/_jsxs(Flex, {
        gap: "100",
        children: [value === 'OPEN' ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customer-portal-app.status.open"
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customer-portal-app.status.closed"
        }), stage && /*#__PURE__*/_jsxs("span", {
          children: ["| ", stage]
        })]
      })
    });
  }
  if (value === '') {
    return null;
  }
  if (crmPropertyType === 'enumeration' && typeof value === 'boolean') {
    return value ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: "customer-portal-app.yes"
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "customer-portal-app.no"
    });
  }
  if (crmPropertyType === 'number' && numberDisplayHint === 'duration' && typeof value === 'number') {
    return /*#__PURE__*/_jsx(FormattedDuration, {
      to: value + Date.now()
    });
  }
  if (crmPropertyType === 'date' && typeof value === 'number') {
    return formatDate(value);
  }
  if (crmPropertyType === 'datetime' && typeof value === 'number') {
    return formatDateTime(value);
  }
  if (crmPropertyType === 'string' && typeof value === 'string') {
    return /*#__PURE__*/_jsx("span", {
      title: value,
      children: value
    });
  }
  if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
    return value.join(', ');
  }
  return value;
};
export const formatCell = (property, isGrid = false) => cell => {
  const value = cell.getValue();
  return renderPropertyValue(value, property, cell.row.original, isGrid);
};